import type {
  CurrentMentalHealthConditionOptionId,
  NeurodivergentOptionId,
  PastMentalHealthConditionOptionId,
  ReceivedMentalSupport,
  SelectableItem,
  YesNo,
} from '@physitrack/championhealth-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../questions/checkbox/Checkbox';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function MentalHealth5({
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onOpenModal,
  onSetValue,
}: AssessmentPageItem) {
  const { t } = useTranslation();
  const mentalDiagnosisOptions: SelectableItem<YesNo>[] = [
    { id: 'yes', title: t('MH033') },
    { id: 'no', title: t('MH034') },
  ];

  const mentalHealthOptions: SelectableItem<CurrentMentalHealthConditionOptionId>[] = [
    { id: 'none', title: t('MH035') },
    { id: 'anxiety', title: t('MH036') },
    { id: 'depression', title: t('MH037') },
    { id: 'panic', title: t('MH038') },
    { id: 'bipolar', title: t('MH039') },
    { id: 'ocd', title: t('MH040') },
    { id: 'social_anxiety', title: t('MH041') },
    { id: 'ptsd', title: t('MH042') },
    { id: 'phobia', title: t('MH043') },
    { id: 'eating', title: t('MH044') },
    { id: 'schizophrenia', title: t('MH045') },
    { id: 'other', title: t('MH046') },
  ];

  const pastMentalHealthOptions: SelectableItem<PastMentalHealthConditionOptionId>[] = [
    { id: 'none_sq', title: t('MH035') },
    { id: 'anxiety', title: t('MH036') },
    { id: 'depression', title: t('MH037') },
    { id: 'panic', title: t('MH038') },
    { id: 'bipolar', title: t('MH039') },
    { id: 'ocd', title: t('MH040') },
    { id: 'social_anxiety', title: t('MH041') },
    { id: 'ptsd', title: t('MH042') },
    { id: 'phobia', title: t('MH043') },
    { id: 'eating', title: t('MH044') },
    { id: 'schizophrenia', title: t('MH045') },
    { id: 'other', title: t('MH046') },
  ];

  const mentalSupportOptions: SelectableItem<ReceivedMentalSupport>[] = [
    { id: 'currently', title: t('MH047') },
    { id: 'previously', title: t('MH048') },
    { id: 'never', title: t('MH049') },
  ];

  const neurodivergentOptions: SelectableItem<NeurodivergentOptionId>[] = [
    { id: 'none_nd', title: t('MH050') },
    { id: 'asd', title: t('MH051') },
    { id: 'adhd', title: t('MH052') },
    { id: 'dyslexia', title: t('MH053') },
    { id: 'dyscalculia', title: t('MH054') },
    { id: 'dyspraxia', title: t('MH055') },
    { id: 'other_nd', title: t('MH056') },
  ];

  useEffect(() => {
    if (assessmentValues.mental_health_diagnosis === 'no') {
      onSetValue({
        id: 'current_mental_health_conditions',
        value: null,
      });
      onSetValue({
        id: 'past_mental_health_conditions',
        value: null,
      });
    }
  }, [assessmentValues.mental_health_diagnosis]);

  return (
    <div className="question_container" id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>

      <Radio
        questionid="mental_health_diagnosis"
        width=""
        questionTitle={t('assessment.question.mental_health_diagnosis')}
        options={mentalDiagnosisOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        onOpenModal={onOpenModal}
      />
      {assessmentValues.mental_health_diagnosis === 'yes' && (
        <Checkbox
          questionid="current_mental_health_conditions"
          width=""
          questionTitle={t('assessment.question.current_mental_health_conditions')}
          questionTitleSuffix={t('MH059')}
          options={mentalHealthOptions}
          itemLimit="2"
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          fadeIn="yes"
        />
      )}
      {assessmentValues.mental_health_diagnosis === 'yes' &&
        assessmentValues.current_mental_health_conditions != null && (
          <Checkbox
            questionid="past_mental_health_conditions"
            width=""
            questionTitle={t('assessment.question.past_mental_health_conditions')}
            questionTitleSuffix={t('MH062')}
            options={pastMentalHealthOptions}
            itemLimit="2"
            exclude={assessmentValues.current_mental_health_conditions}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
            secondaryQ
            fadeIn="yes"
          />
        )}
      <Radio
        questionid="received_mental_support"
        width=""
        questionTitle={t('assessment.question.received_mental_support')}
        options={mentalSupportOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('MH065')}
        onOpenModal={onOpenModal}
      />
      <Checkbox
        questionid="neurodivergent"
        width="_wide"
        questionTitle={t('assessment.question.neurodivergent')}
        questionTitleSuffix={t('MH067')}
        options={neurodivergentOptions}
        itemLimit="2"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
    </div>
  );
}
